import { IUserLoginPayload } from "../../../types/api/user";
import { useUserLogin } from "../../../hooks/api/user/hooks";
import { Button, Checkbox, Col, Form, Input, message, Row } from "antd";
import { LockOutlined, MailOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../store/slices/user";
import { useEffect, useState } from "react";
import axiosInstance from "../../../lib/axios";

const REMEMBERED_EMAIL_KEY = "onap_remembered_email";

export default function Login() {
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [tempToken, setTempToken] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [remember, setRemember] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  const { updateToken } = useUserStore();

  // Pre-fill the last remembered email, if any, and default the checkbox on.
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (savedEmail) {
      form.setFieldsValue({ email: savedEmail });
      setRemember(true);
    }
  }, [form]);

  const { mutate, isPending } = useUserLogin({
    onSuccess: (res: any) => {
      if (res?.requires_2fa) {
        setTempToken(res.temp_token ?? "");
        setStep("otp");
        return;
      }
      if (remember) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, pendingEmail);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      updateToken({
        token: res.data.token,
        user: res.data.userInfo,
        remember,
      });
      navigate("/");
    },

    onError: (err: any) => {
      messageApi.error(err.message || "Login failed");
    },
  });

  const handleVerifyOtp = async () => {
    try {
      const { otp_token } = await otpForm.validateFields();
      setOtpVerifying(true);
      const res = await axiosInstance.post("/api/company/v1/admin/2fa/verify-login", {
        temp_token: tempToken,
        otp_token,
      });
      const data = res.data?.data ?? res.data;
      if (remember) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, pendingEmail);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      updateToken({ token: data.token, user: data.userInfo, remember });
      navigate("/");
    } catch (err: any) {
      messageApi.error(err?.message || "Invalid OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {contextHolder}

      {/* LEFT BRAND PANEL */}
      <div className="hidden md:flex w-1/2 bg-slate-950 relative items-center justify-center overflow-hidden">

        {/* Background Image */}
        <img
          src="/login.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15 blur-sm scale-105"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-blue-950/90" />

        {/* Glow Effects */}
        <div className="absolute top-[-120px] left-[-120px] w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-[-100px] right-[-100px] w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl" />

        <div className="relative z-10 text-white text-center px-10">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/sitepro_logo.png"
              className="w-50 h-20 "
              alt="ARRUK"
            />
          </div>

         

          <p className="text-slate-300 mt-4 text-sm leading-relaxed max-w-md">
            UK Field Service Management Platform for Jobs,
            Engineers & Operations
          </p>

          <div className="mt-8 text-xs text-slate-400 tracking-wide">
            Smart scheduling • Live tracking • Job management
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="relative flex w-full md:w-1/2 items-center justify-center overflow-hidden bg-slate-100">

        {/* Background Image */}
        <img
          src="/light_logo.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-slate-50/95 to-blue-50/90" />

        <div className="relative z-10 w-full max-w-md px-6">
          <div className="bg-white/90 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/40">

            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="w-[150px] h-15  flex items-center justify-center ">
                {step === "otp" ? (
                  <SafetyCertificateOutlined className="text-white text-2xl" />
                ) : (
                  <img
                    src="/dark_logo.png"
                    alt="logo"
                    className="w-full h-full object-fill"
                  />
                )}
              </div>
            </div>

            {step === "credentials" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Sign in to ARRUK
                </h2>

                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Access your operations dashboard
                </p>

                <Form
                  form={form}
                  layout="vertical"
                  size="large"
                  onFinish={(values: IUserLoginPayload) => {
                    setPendingEmail(values.email);
                    mutate(values);
                  }}
                >
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Form.Item
                        name="email"
                        rules={[{ required: true, message: "Email is required" }]}
                      >
                        <Input
                          placeholder="Admin Email"
                          prefix={<MailOutlined />}
                          className="rounded-xl h-12"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="password"
                        rules={[{ required: true, message: "Password is required" }]}
                      >
                        <Input.Password
                          placeholder="Password"
                          prefix={<LockOutlined />}
                          className="rounded-xl h-12"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <div className="flex items-center justify-between -mt-1 mb-1">
                        <Checkbox
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                        >
                          <span className="text-sm text-slate-600">Remember me</span>
                        </Checkbox>
                      </div>
                    </Col>

                    <Col span={24}>
                      <Button
                        htmlType="submit"
                        loading={isPending}
                        className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none shadow-lg"
                      >
                        Login to Dashboard
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            ) : (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Two-Factor Authentication
                </h2>

                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Enter the 6-digit code from your authenticator app
                </p>

                <Form form={otpForm} layout="vertical" size="large">
                  <Form.Item
                    name="otp_token"
                    rules={[
                      { required: true, message: "OTP is required" },
                      { len: 6, message: "Must be 6 digits" },
                      { pattern: /^\d{6}$/, message: "Digits only" },
                    ]}
                  >
                    <Input
                      placeholder="000000"
                      maxLength={6}
                      className="rounded-xl h-12 text-center text-2xl tracking-[0.5em] font-mono"
                      autoFocus
                    />
                  </Form.Item>

                  <div className="flex gap-3 mt-2">
                    <Button
                      onClick={() => { setStep("credentials"); otpForm.resetFields(); }}
                      className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleVerifyOtp}
                      loading={otpVerifying}
                      className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none shadow-lg"
                    >
                      Verify
                    </Button>
                  </div>
                </Form>
              </>
            )}

            <div className="text-center text-xs text-slate-400 mt-6">
              ARRUK © {new Date().getFullYear()} • UK Field Service Platform
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}